import { Box, Grid2 } from '@mui/material';
import * as Sentry from '@sentry/gatsby';
import React, { useMemo } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import type {
  ChartData,
  Dataset,
} from '@components/Charts/Bar/StackedBarChart';

import {
  BLOOD_PRESSURE,
  BODY_HEIGHT,
  getObservationConfig,
} from '@constants/fhir';

import { useConversion } from '@hooks/useConversion';
import { useHealthChart } from '@hooks/useHealthChart';
import { useMetricWithinRange } from '@hooks/useMetricWithinRange';
import useReferenceRange from '@hooks/useReferenceRange';

import type { ObservationRecord } from '@models/observation';

import { convertUnit } from '@utils/conversion';
import { getHeightMetres, toDisplayHeight } from '@utils/height';

import { ReferenceRangeRiskLevel } from 'generated/types';

import KpiCard from './KpiCard';

const messages = defineMessages({
  start: {
    id: 'healthTracker.detail.kpi.start',
    defaultMessage: 'First',
  },
  end: {
    id: 'healthTracker.detail.kpi.end',
    defaultMessage: 'Last',
  },
  change: {
    id: 'healthTracker.detail.kpi.change',
    defaultMessage: 'Change',
  },
  high: {
    id: 'healthTracker.detail.kpi.high',
    defaultMessage: 'High',
  },
  low: {
    id: 'healthTracker.detail.kpi.low',
    defaultMessage: 'Low',
  },
  percentage: {
    id: 'healthTracker.detail.kpi.percentage',
    defaultMessage: '% in parameters',
  },
  sys: {
    id: 'healthTracker.detail.kpi.bp.systolic',
    defaultMessage: 'Sys',
  },
  dia: {
    id: 'healthTracker.detail.kpi.bp.diastolic',
    defaultMessage: 'Dia',
  },
});

interface KpiComponent {
  /** KPI title */
  descriptor: MessageDescriptor;
  /** First, last, highest, lowest values of a metric set */
  value: number[];
}
export interface Kpi extends KpiComponent {
  /** Values used to determine which ref range it is in */
  valueInRange?: number[];
  /** KPI components (used for BP) */
  components?: KpiComponent[];
  /** KPI chart data */
  data?: ChartData;
}

/** Body height, normalised to metres then converted once for display. */
function heightValues(
  record: ObservationRecord,
  system: string
): number[] | null {
  const metres = getHeightMetres(record);
  return metres === null ? null : [toDisplayHeight(metres, system)];
}

/**
 * Every other vital sign keeps its existing contract: the raw value is in
 * whatever unit the record declares (weight may be lb, temperature °F), and
 * `convertUnit` resolves the direction.
 */
function rawValues(
  record: ObservationRecord,
  system: string,
  code: string,
  precision?: number
): number[] | null {
  const { unit, value } = record;
  if (
    (!value && typeof value !== 'number') ||
    typeof value === 'string' ||
    typeof value === 'boolean'
  ) {
    return null;
  }
  return (Array.isArray(value) ? value : [value]).map((val) =>
    Number(convertUnit(val, unit, system, precision, code))
  );
}

export default function DrilldownKpi() {
  const {
    state: { conversion },
  } = useConversion();
  const { code, data, referenceRangeMap } = useHealthChart();
  const { parameterMap } = useReferenceRange();
  const { determineMetricRange } = useMetricWithinRange();
  const precision = getObservationConfig(code)?.precision;
  const hasParams = referenceRangeMap.size > 0;

  const metricRanges = data.reduce<Record<ReferenceRangeRiskLevel, number>>(
    (acc, { type, value }) => {
      const range = determineMetricRange(value, type);
      if (range) {
        acc[range]++;
        return acc;
      }
      return acc;
    },
    {
      [ReferenceRangeRiskLevel.low]: 0,
      [ReferenceRangeRiskLevel.normal]: 0,
      [ReferenceRangeRiskLevel.mild]: 0,
      [ReferenceRangeRiskLevel.moderate]: 0,
      [ReferenceRangeRiskLevel.high]: 0,
      [ReferenceRangeRiskLevel.crisis]: 0,
      [ReferenceRangeRiskLevel.below_target]: 0,
      [ReferenceRangeRiskLevel.target]: 0,
      [ReferenceRangeRiskLevel.ideal]: 0,
    }
  );

  const chartData: ChartData = {
    datasets: Object.entries(metricRanges)
      .filter(([, v]) => v > 0)
      .map<Dataset>(([k, v]) => {
        const param = k as ReferenceRangeRiskLevel;
        const quantity = (v / data.length) * 100;
        return {
          label: k,
          data: [quantity],
          backgroundColor: parameterMap[param].colour,
          hoverBackgroundColor: parameterMap[param].colour,
        };
      }),
  };

  const isBloodPressure = code === BLOOD_PRESSURE;
  const isBodyHeight = code === BODY_HEIGHT;

  const { start, end, high, low } = useMemo(() => {
    const kpis = data.reduce<{
      start: number[];
      end: number[];
      high: number[];
      low: number[];
    }>(
      (acc, { record }, index) => {
        const system = conversion[code] ? 'CON' : 'SI';
        const values = isBodyHeight
          ? // Height is stored either as metres or as a feet + inches component
            // pair, so normalise to metres before any comparison and convert
            // once for display. Unrounded, so a real change survives even when
            // both heights render as the same ft/in text.
            heightValues(record, system)
          : rawValues(record, system, code, precision);
        if (!values) {
          return acc;
        }
        // Set last item as the oldest metric
        if (index === data.length - 1) {
          acc.start = values;
        }
        // Set first item as the latest metric
        if (index === 0) {
          acc.end = values;
          acc.high = values;
          acc.low = values;
          return acc;
        }

        // Compare
        // For blood pressure: Highest and lowest systolic value of a reading
        if (acc.high[0] < values[0]) {
          acc.high = values;
          return acc;
        }
        if (acc.low[0] > values[0]) {
          acc.low = values;
          return acc;
        }
        /*
        if (values.some((v, i) => v > acc.high[i])) {
          acc.high = values.map((v, i) => (v > acc.high[i] ? v : acc.high[i]));
          return acc;
        }
        if (values.some((v, i) => v < acc.low[i])) {
          acc.low = values.map((v, i) => (v < acc.low[i] ? v : acc.low[i]));
          return acc;
        }
          */
        return acc;
      },
      {
        start: [],
        end: [],
        high: [],
        low: [],
      }
    );
    return kpis;
  }, [code, conversion, data, isBodyHeight, precision]);

  // Normalised readings always have a fixed shape - one value for height, two
  // for blood pressure - so a mismatch means the data is malformed. Show the
  // placeholder rather than silently differencing part of a reading.
  const changeShapesMatch = start.length > 0 && start.length === end.length;
  if (!changeShapesMatch && start.length && end.length) {
    Sentry.captureException(
      `[${code}] cannot compute change: first has ${start.length} value(s), last has ${end.length}`
    );
  }

  const kpis: Kpi[] = [
    {
      descriptor: messages.start,
      value: start.map((val) => val),
    },
    {
      descriptor: messages.end,
      value: end.map((val) => val),
    },
    {
      descriptor: messages.change,
      value: changeShapesMatch ? end.map((end_i, i) => end_i - start[i]) : [],
      // Want 'Change' section colours to match the 'End' metric colours
      valueInRange: end.map((val) => val),
    },
    ...(isBloodPressure
      ? [
          {
            descriptor: messages.high,
            value: high.map((val) => val),
            components: [
              {
                descriptor: messages.sys,
                value: high.filter((_, idx) => idx === 0),
              },
              {
                descriptor: messages.dia,
                value: high.filter((_, idx) => idx === high.length - 1),
              },
            ],
          },
          {
            descriptor: messages.low,
            value: low.map((val) => val),
            components: [
              {
                descriptor: messages.sys,
                value: low.filter((_, idx) => idx === 0),
              },
              {
                descriptor: messages.dia,
                value: low.filter((_, idx) => idx === low.length - 1),
              },
            ],
          },
        ]
      : [
          {
            descriptor: messages.high,
            value: high.map((val) => val),
          },
          {
            descriptor: messages.low,
            value: low.map((val) => val),
          },
        ]),
    {
      descriptor: messages.percentage,
      value: [],
      data: chartData,
    },
  ];

  const hasChartData = chartData.datasets.length;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid2 container spacing={2}>
        {kpis.map((kpi, index) => {
          const { descriptor } = kpi;
          const isChart = descriptor.id === messages.percentage.id;

          if (isChart && !hasChartData) {
            return null;
          }
          return (
            <Grid2
              key={`${descriptor.id}-${index}`}
              size={{
                xs: isChart || index === 2 ? 12 : 6,
                sm: isChart ? 12 : 2.4,
                lg: hasParams ? (isChart ? 4.5 : 1.5) : 2.4,
              }}
            >
              <KpiCard key={`kpi-${kpi.descriptor.id}`} kpi={kpi} />
            </Grid2>
          );
        })}
      </Grid2>
    </Box>
  );
}
